/**
 * <khaime-product-card> Web Component
 * Displays a single product card with configurable features
 */

import { KhaimeBaseElement, BASE_STYLES, ComponentRegistry } from '../base';
import { PRODUCT_CARD_STYLES } from './product-card-styles';
import type { Product, SDKConfig } from '../../types';
import { formatPrice, getProductBadge } from '../../types';
import { escapeHtml } from '../../utils';
import { createKhaimeEvent } from '../../types/events';

export class KhaimeProductCardElement extends KhaimeBaseElement {
  static tagName = 'khaime-product-card';

  private _product: Product | null = null;
  private _currency: string = 'USD';

  static get observedAttributes(): string[] {
    return [
      ...super.observedAttributes,
      'product-id',
      'currency',
      'layout',
    ];
  }

  /**
   * Set product data programmatically
   */
  set product(value: Product) {
    this._product = value;
    if (this._initialized) {
      this.render();
    }
  }

  get product(): Product | null {
    return this._product;
  }

  /**
   * Set currency
   */
  set currency(value: string) {
    this._currency = value;
    if (this._initialized) {
      this.render();
    }
  }

  get currency(): string {
    return this._currency;
  }

  /**
   * Override to skip API initialization for cards (parent handles it)
   */
  protected async initializeApi(): Promise<void> {
    // Cards don't need their own API client - they get data from parent
  }

  /**
   * Override to skip config loading (use parent's config)
   */
  protected async loadConfig(): Promise<void> {
    // Apply attribute overrides only
    this.applyAttributeOverrides();
  }

  /**
   * Set config from parent component
   */
  public setConfig(config: SDKConfig): void {
    this.config = config;
    if (this._initialized) {
      this.render();
    }
  }

  /**
   * Render the product card
   */
  protected render(): void {
    if (!this._product) {
      this.shadow.innerHTML = `
        <style>${BASE_STYLES}${PRODUCT_CARD_STYLES}</style>
        <div class="ks-loading"><div class="ks-spinner"></div></div>
      `;
      return;
    }

    const p = this._product;
    const cardStyle = this.config.layout.cardStyle;
    const showPrices = this.config.features.showPrices;
    const showRatings = this.config.features.showRatings;
    const showDescription = this.config.features.showDescription;
    const quickBuy = this.config.features.quickBuy;
    const showBadges = this.config.features.showBadges;
    const hoverAnimation = this.config.effects.hoverAnimation;

    // Set data attribute for hover animation
    this.dataset.ksHover = hoverAnimation;

    const isOutOfStock = p.stock != null && p.stock <= 0;
    const badge = isOutOfStock ? 'Out of Stock' : (showBadges ? getProductBadge(p) : null);
    const badgeClass = isOutOfStock ? 'ks-badge--out-of-stock' : (p.is_slash_price ? 'ks-badge--sale' : `ks-badge--${p.type}`);
    const initial = (p.title || '?').charAt(0).toUpperCase();

    // Set out-of-stock attribute for host-level dimming
    if (isOutOfStock) {
      this.dataset.ksOutOfStock = '';
    } else {
      delete this.dataset.ksOutOfStock;
    }

    this.shadow.innerHTML = `
      <style>${BASE_STYLES}${PRODUCT_CARD_STYLES}</style>
      <article class="ks-card ks-card--${cardStyle}" part="card">
        <div class="ks-card-image" part="image">
          ${p.thumbnail
            ? `<img
                src="${escapeHtml(p.thumbnail)}"
                alt="${escapeHtml(p.title)}"
                loading="lazy"
                part="img"
                onerror="this.style.display='none';this.nextElementSibling.style.display='flex';"
              />
              <div class="ks-placeholder ks-placeholder--hidden" part="placeholder">
                <span class="ks-placeholder-letter">${escapeHtml(initial)}</span>
              </div>`
            : `<div class="ks-placeholder" part="placeholder">
                <span class="ks-placeholder-letter">${escapeHtml(initial)}</span>
              </div>`
          }
          ${badge ? `<span class="ks-badge ${badgeClass}" part="badge">${escapeHtml(badge)}</span>` : ''}
          <button class="ks-wishlist" part="wishlist" aria-label="Add to wishlist">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
            </svg>
          </button>
        </div>
        <div class="ks-card-body" part="body">
          <h3 class="ks-card-title" part="title">${escapeHtml(p.title)}</h3>
          ${showDescription && p.description
            ? `<p class="ks-card-desc" part="description">${escapeHtml(p.description)}</p>`
            : ''
          }
          ${showRatings && p.rating
            ? `<div class="ks-card-rating" part="rating">
                <div class="ks-stars">${this.renderStars(p.rating)}</div>
                ${p.reviews_count ? `<span class="ks-rating-count">(${p.reviews_count})</span>` : ''}
              </div>`
            : ''
          }
          ${showPrices
            ? `<div class="ks-card-price" part="price">
                <span class="ks-price">${formatPrice((p.is_slash_price && p.slash_price ? p.slash_price : p.price) / 100, p.currency || this._currency)}</span>
                ${p.is_slash_price && p.slash_price
                  ? `<span class="ks-price-original">${formatPrice(p.price / 100, p.currency || this._currency)}</span>`
                  : ''
                }
              </div>`
            : ''
          }
          ${quickBuy
            ? `<button class="ks-quick-buy" part="quick-buy" ${isOutOfStock ? 'disabled' : ''}>${
                isOutOfStock ? 'Out of Stock'
                : (p.variants && p.variants.length > 0) ? 'Select options'
                : p.type === 'physical_product' ? 'Add to cart'
                : 'Buy Now'
              }</button>`
            : ''
          }
        </div>
      </article>
    `;
  }

  /**
   * Render star rating
   */
  private renderStars(rating: number): string {
    const fullStars = Math.floor(rating);
    const emptyStars = 5 - fullStars;

    const starSvg = `<svg class="ks-star" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
    </svg>`;

    const emptyStarSvg = `<svg class="ks-star ks-star--empty" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
    </svg>`;

    return starSvg.repeat(fullStars) + emptyStarSvg.repeat(emptyStars);
  }

  /**
   * Set up event listeners
   */
  protected setupEventListeners(): void {
    // Card click
    this.shadow.querySelector('.ks-card')?.addEventListener('click', (e) => {
      // Don't trigger if clicking quick buy or wishlist
      const target = e.target as HTMLElement;
      if (target.closest('.ks-quick-buy') || target.closest('.ks-wishlist')) {
        return;
      }

      if (this._product) {
        this.dispatchEvent(
          createKhaimeEvent('khaime:product-click', {
            product: this._product,
            source: 'card',
            merchantId: this.merchantId,
          })
        );
      }
    });

    // Quick buy click — physical products add to cart, others go to direct checkout
    this.shadow.querySelector('.ks-quick-buy')?.addEventListener('click', (e) => {
      e.stopPropagation();
      if (this._product) {
        const eventName = this._product.type === 'physical_product' ? 'khaime:add-to-cart' : 'khaime:buy-now';
        this.dispatchEvent(
          createKhaimeEvent(eventName, {
            product: this._product,
            quantity: 1,
            merchantId: this.merchantId,
          })
        );
      }
    });

    // Wishlist click
    this.shadow.querySelector('.ks-wishlist')?.addEventListener('click', (e) => {
      e.stopPropagation();
      const btn = e.currentTarget as HTMLElement;
      btn.classList.toggle('active');
    });
  }
}

// Register the component
ComponentRegistry.define('khaime-product-card', KhaimeProductCardElement);

export default KhaimeProductCardElement;
